import React, { useCallback, useRef, useState } from "react";
import {
  View,
  StyleSheet,
  Alert,
  InteractionManager,
  FlatList,
  TouchableOpacity,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import DraggableFlatList, {
  RenderItemParams,
} from "react-native-draggable-flatlist";
import { useLikedNames } from "../context/LikedNamesContext";
import LikedNameItem from "../components/LikedNameItem";
import CustomText from "../components/CustomText";
import { useTranslation } from "../hooks/useTranslation";

const LikedNamesScreen = () => {
  const { likedNames, removeLikedName, reorderLikedNames } = useLikedNames();
  const flatListRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const { getTranslation } = useTranslation();
  const navigation = useNavigation();

  const confirmDelete = useCallback(
    (name: string) => {
      Alert.alert(
        "Confirm Delete",
        `Are you sure you want to remove "${name}" from the liked names?`,
        [
          { text: getTranslation("ID_CANCEL"), style: "cancel" },
          {
            text: "Delete",
            style: "destructive",
            onPress: () => removeLikedName(name),
          },
        ]
      );
    },
    [removeLikedName]
  );

  const handleDragEnd = useCallback(
    ({ data, from, to }) => {
      if (from !== to) {
        InteractionManager.runAfterInteractions(() => {
          reorderLikedNames(data);
          setIsDragging(false);
        });
      } else {
        setIsDragging(false);
      }
    },
    [reorderLikedNames]
  );

  const handleNavigate = useCallback(
    (item) => {
      console.log("Navigating to NameDetails with item:", item);
      navigation.navigate("NameDetails", { nameDetails: item });
    },
    [navigation]
  );

  const renderItem = useCallback(
    ({ item, drag, isActive }: RenderItemParams<any>) => (
      <LikedNameItem
        item={item}
        drag={drag}
        isActive={isActive}
        onDelete={confirmDelete}
        onNavigate={handleNavigate}
      />
    ),
    [confirmDelete, handleNavigate]
  );

  return (
    <View style={styles.container}>
      {likedNames.length === 0 ? (
        <View style={styles.emptyContainer}>
          <CustomText style={styles.emptyText}>
            {getTranslation("ID_EMPTY_LIST")}
          </CustomText>
        </View>
      ) : (
        <DraggableFlatList
          data={likedNames}
          keyExtractor={(item) => item.id}
          onDragBegin={() => setIsDragging(true)}
          onDragEnd={handleDragEnd}
          renderItem={renderItem}
          activationDistance={5}
          maxToRenderPerBatch={5}
          updateCellsBatchingPeriod={100}
          windowSize={5}
          scrollEnabled={!isDragging}
          dragHitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          simultaneousHandlers={flatListRef}
          containerStyle={{ flex: 1 }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: "#F9FAFB",
    justifyContent: "center",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  emptyText: {
    fontSize: 18,
    color: "#666",
    textAlign: "center",
    fontFamily: "Urbanist_400Regular",
  },
  item: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  text: {
    fontSize: 18,
    color: "#333",
  },
});

export default LikedNamesScreen;
